// The care plan is the ONLY author of a target — `care_goal` is retired (2026-08-26);
// its legacy rows are dead data, never read one for a target. Pinned by static S-122.
// Mirrored by the patient companion spark (parity: scripts/check-care-plan-target-parity.mjs)
// so the two surfaces share one vocabulary.

import {
  foldPlanTargets,
  goalFieldsFromPlanTarget,
  isComparableOperator,
  measureTypeForPlanTarget,
  planTargetForMeasure,
  type PlanTargetEntry,
} from '../../../scripts/care-plan-target-map.mjs';

export type { PlanTargetEntry };
export {
  foldPlanTargets, goalFieldsFromPlanTarget, isComparableOperator, measureTypeForPlanTarget, planTargetForMeasure,
};

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * The authored numeric target for a measure, or null.
 *
 * Honours the `a1c` → `hba1c` alias, and returns null for a `reduce_by_pct` target —
 * its value is a PERCENTAGE, and treating it as an absolute is how a weight goal came to
 * draw a 7 kg (15 lb) target line on a chart of ~190 lb readings.
 */
export function planTargetNum(targets: unknown, measureType: string): number | null {
  return num(goalFieldsFromPlanTarget(planTargetForMeasure(targets, measureType)).target_value_num);
}

/** The authored display string for a measure ("<55 mg/dL (secondary prev)"), or null. */
export function planTargetText(targets: unknown, measureType: string): string | null {
  const e = planTargetForMeasure(targets, measureType);
  return e && e.display != null ? String(e.display) : null;
}

/** Re-key an authored targets object into the measure vocabulary, dropping the no-goal class. */
export function foldedPlanTargets(targets: unknown): Record<string, PlanTargetEntry> {
  return foldPlanTargets(targets).targets;
}

/**
 * Is this measure at its authored target?
 *
 * Computed, never read from `care_goal.status`. That stored verdict is written by two
 * different workflow nodes with two different meanings — one of them sets 'on_target' from
 * the mere presence of a reading, with no target comparison at all — and the renderers
 * trust it ahead of the numbers, forcing a progress bar to 100%. Returns null when the
 * question cannot be answered rather than guessing.
 */
export function isAtPlanTarget(targets: unknown, measureType: string, value: unknown): boolean | null {
  const entry = planTargetForMeasure(targets, measureType);
  if (!entry || !isComparableOperator(entry.operator)) return null;
  const t = num(entry.value);
  const v = num(value);
  if (t == null || v == null) return null;
  const op = String(entry.operator).trim();
  // '≥' and '≤' are comparable per isComparableOperator AND are what TARGET_DEFS declares
  // (egfr: '≥'), so an ASCII-only test sent every ≥ target down the <= branch: eGFR 38
  // against a goal of ≥45 answered "on target" on the patient's own screen.
  return op === '>=' || op === '>' || op === '≥' ? v >= t : v <= t;
}
